import {
  BillingTransferInfo,
  getTransferParticipantNames,
  normalizeTransferDisplayName,
  TRANSFER_REASON,
} from '@/utils/billingTransfer'

export interface TransferDisplayParams {
  transferInfo: BillingTransferInfo
  billUserId?: number
  memberName: string
  t: (key: string, options?: Record<string, unknown>) => string
}

export interface TransferDisplayResult {
  typeStr: string
  memberName: string
}

export function getTransferDisplay({
  transferInfo,
  billUserId,
  memberName,
  t,
}: TransferDisplayParams): TransferDisplayResult | null {
  const { fromUser: fromUserId, toUser: toUserId, reason } = transferInfo

  const ownerName = normalizeTransferDisplayName(
    memberName,
    typeof billUserId === 'number' ? String(billUserId) : '-',
  )
  const { fromUserName, toUserName } = getTransferParticipantNames(transferInfo)

  const ownerIsFromUser = typeof billUserId === 'number' && billUserId === fromUserId
  const ownerIsToUser = typeof billUserId === 'number' && billUserId === toUserId

  const label = (key: string, name: string, defaultValue: string) => ({
    typeStr: t(`teamBilling.transfer.${key}`, { name, defaultValue }),
    memberName: ownerName,
  })

  switch (reason) {
    case TRANSFER_REASON.RechargeMemberCredits:
      if (ownerIsToUser) {
        return label('memberRecharge', fromUserName, `Credit Recharge: ${fromUserName}`)
      }
      if (ownerIsFromUser) {
        return label('adminRechargedTo', toUserName, `Credit Recharged to ${toUserName}`)
      }
      return null

    case TRANSFER_REASON.ReclaimMemberCredits:
      if (ownerIsFromUser) {
        return label('memberReclaim', toUserName, `Credit Reclaim: ${toUserName}`)
      }
      if (ownerIsToUser) {
        return label('adminReclaimedFrom', fromUserName, `Credit Reclaimed from ${fromUserName}`)
      }
      return null

    case TRANSFER_REASON.RemoveMemberResource:
      if (ownerIsFromUser) {
        return {
          typeStr: t('teamBilling.transfer.removalReclaimSelf', {
            defaultValue: 'Credit Reclaim (member removed)',
          }),
          memberName: ownerName,
        }
      }
      if (ownerIsToUser) {
        return label(
          'removalReclaimFrom',
          fromUserName,
          `Credit Reclaimed from removed member ${fromUserName}`,
        )
      }
      return null

    case TRANSFER_REASON.TransferCreditsBetweenScopes:
      if (ownerIsFromUser) {
        return {
          typeStr: t('teamBilling.transfer.scopeTransferToTeam', {
            defaultValue: 'Transfer to team account',
          }),
          memberName: ownerName,
        }
      }
      if (ownerIsToUser) {
        return {
          typeStr: t('teamBilling.transfer.scopeTransferFromSelf', {
            defaultValue: 'Received from personal account',
          }),
          memberName: ownerName,
        }
      }
      return null

    default:
      return null
  }
}
